import { graphQlManager } from "../services/server";
import { Bundle, BundlesFeed, Transaction } from "../types";
import { duplicateBundleMutation, duplicateTransactionMutation, getCurrentUserDetailsQuery, getCustomerBundlesQuery, getCustomerTransactionsQuery, getTemplateBundlesQuery, getTransactionsForBundleQuery, setBundleName, setTransactionDesignName } from "./queries";

export const nameBundle = async (id: string, name: string, context: any) => {
    return await graphQlManager.getShadowGraphqlClient().mutate<object>({
        mutation: setBundleName,
        errorPolicy: "all",
        variables: {
            id,
            name,
        },
        context,
    });
};

export const nameTransaction = async (id: string, name: string) => {
    return await graphQlManager.getShadowGraphqlClient().mutate<object>({
        mutation: setTransactionDesignName,
        errorPolicy: "all",
        variables: {
            id,
            name,
        },
    });
};

export const duplicateBundle = async (id: string, template: boolean) => {
    const dupe = await graphQlManager.getShadowGraphqlClient().mutate<{ bundleDuplicate: Bundle }>({
        mutation: duplicateBundleMutation,
        errorPolicy: "all",
        variables: {
            id,
            template,
        },
    });
    return dupe;
};

export const duplicateTransaction = async (id: string, template: boolean) => {
    const dupe = await graphQlManager
    .getShadowGraphqlClient()
    .mutate<{ transactionDuplicate: Transaction }>({
        mutation: duplicateTransactionMutation,
        errorPolicy: "all",
        variables: {
            id,
            template,
        },
    });
    return dupe;
};

export const getCustomer = async (email: string) => {
    const queryResponse = await graphQlManager.getShadowGraphqlClient().query<{ customer: { id: string } }>({
        query: getCurrentUserDetailsQuery,
        errorPolicy: "all",
        variables: {
            email,
        },
    });
    return queryResponse.data.customer;
};

export const getCustomerBundles = async (customerId: string, limit: number, offset: number) => {
    const rows = await graphQlManager.getShadowGraphqlClient().query<{
        customerBundlesFeed: BundlesFeed;
    }>({
        query: getCustomerBundlesQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            id: customerId,
            offset,
            limit,
        },
    });
    return rows;
};

export const getTransactionsForBundle = async (id: string, context: any) => {
    return await graphQlManager.getShadowGraphqlClient().query<{
        bundles: { id: string; transactions: Transaction[] }[];
    }>({
        query: getTransactionsForBundleQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            id,
        },
        context,
    });
};

interface TransactionsFeed {
    items: Transaction[];
    total: number;
}

export const getUnorderedTransactions = async (customerId: string, limit: number, offset: number) => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        customerTransactionsFeed: TransactionsFeed;
    }>({
        query: getCustomerTransactionsQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            id: customerId,
            quicksearch: "",
            filters: {
                link: "and",
                status: {
                    operator: "isAnyOf",
                    values: ["Created", "DesignCompleted", "ExternallyOrdered"],
                },
                template: {
                    value: "false",
                },
            },
            offset,
            limit,
        },
    });
    return response?.data.customerTransactionsFeed?.items || [];
};

export const getOrderedTransactions = async (customerId: string, limit: number, offset: number) => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        customerTransactionsFeed: TransactionsFeed;
    }>({
        query: getCustomerTransactionsQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            id: customerId,
            quicksearch: "",
            filters: {
                link: "and",
                status: {
                    operator: "equals",
                    value: "Ordered",
                },
                template: {
                    value: "false",
                },
            },
            offset,
            limit,
        },
    });
    return response?.data.customerTransactionsFeed?.items || [];
};

export const getTemplateTransactions = async (customerId: string, limit: number, offset: number) => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        customerTransactionsFeed: TransactionsFeed;
    }>({
        query: getCustomerTransactionsQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            id: customerId,
            quicksearch: "",
            filters: {
                link: "and",
                template: {
                    value: "true",
                },
            },
            offset,
            limit,
        },
    });
    return response?.data.customerTransactionsFeed?.items || [];
};

export const getTemplateBundles = async (customerId: string, limit: number, offset: number) => {
    const rows = await graphQlManager.getShadowGraphqlClient().query<{
        customerBundlesFeed: BundlesFeed;
    }>({
        query: getTemplateBundlesQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            id: customerId,
            offset,
            limit,
        },
    });
    return rows?.data.customerBundlesFeed?.items || [];
};
